import { useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useParams } from "react-router";
import {
  Calendar,
  Clock,
  Save,
  Play,
  RefreshCw,
  TimerReset,
} from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import {
  useEvergreenSchedule,
  useSaveEvergreenSchedule,
  type EvergreenMode,
  type EvergreenScheduleViewModel,
  type ScheduledReplayConfig,
  type JustInTimeConfig,
  type AutomatedSeriesConfig,
} from "@/hooks/use-webinar-evergreen";
import { formatDateTime } from "@/lib/i18n";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MODE_OPTIONS: Array<{
  value: EvergreenMode;
  label: string;
  desc: string;
}> = [
  { value: "scheduled_replay", label: "Scheduled Replay", desc: "Repeat on specific weekdays and times." },
  { value: "just_in_time", label: "Just-In-Time", desc: "Launch the next session on a rolling interval." },
  { value: "on_demand", label: "On Demand", desc: "Available immediately whenever someone registers." },
  { value: "automated_series", label: "Automated Series", desc: "Run a recurring day-offset sequence." },
];

type DraftState = {
  mode: EvergreenMode;
  scheduledReplay: ScheduledReplayConfig;
  justInTime: JustInTimeConfig;
  automatedSeries: AutomatedSeriesConfig;
  simulatedAttendeeCount: number;
  simulatedAttendeeMin: number;
  simulatedAttendeeMax: number;
};

const DEFAULT_DRAFT: DraftState = {
  mode: "scheduled_replay",
  scheduledReplay: {
    dayOfWeek: [1, 3, 5],
    timeOfDay: "14:00",
    timezone: "UTC",
  },
  justInTime: {
    intervalMinutes: 15,
    availableHoursStart: 9,
    availableHoursEnd: 21,
  },
  automatedSeries: {
    days: [1, 3, 7],
    timeOfDay: "10:00",
    timezone: "UTC",
  },
  simulatedAttendeeCount: 0,
  simulatedAttendeeMin: 0,
  simulatedAttendeeMax: 0,
};

export default function EvergreenConfigPage() {
  const { webinarId } = useParams();
  const { data, isLoading, error, refetch } = useEvergreenSchedule(webinarId);
  const { mutate: saveSchedule, isLoading: isSaving, error: saveError } = useSaveEvergreenSchedule(webinarId);
  const [draft, setDraft] = useState<DraftState | null>(null);

  useEffect(() => {
    if (!data) {
      return;
    }
    setDraft(buildDraftState(data));
  }, [data]);

  if (isLoading && !draft) {
    return <EvergreenSkeleton />;
  }

  if (error && !draft) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      </div>
    );
  }

  const currentDraft = draft ?? DEFAULT_DRAFT;
  const upcomingSessions = data?.upcomingSessions ?? [];

  async function handleSave() {
    if (!webinarId) {
      return;
    }

    const payload = {
      recordingId: data?.recordingId ?? undefined,
      mode: currentDraft.mode,
      config: buildConfigPayload(currentDraft),
      simulatedAttendeeCount: currentDraft.simulatedAttendeeCount,
      simulatedAttendeeRange: currentDraft.simulatedAttendeeMax > 0
        ? {
            min: currentDraft.simulatedAttendeeMin,
            max: Math.max(currentDraft.simulatedAttendeeMin, currentDraft.simulatedAttendeeMax),
          }
        : undefined,
    } as const;

    await saveSchedule(payload);
    refetch();
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Evergreen Config</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Define how this replay behaves for attendees and preview the next sessions before you publish it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {isSaving ? "Saving..." : "Save Config"}
        </button>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
          <Play className="h-4 w-4 text-[var(--color-primary)]" />
          Evergreen Mode
        </h2>
        <div className="grid gap-3 md:grid-cols-4">
          {MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setDraft((prev) => ({ ...(prev ?? DEFAULT_DRAFT), mode: option.value }))}
              className={`rounded-lg border p-3 text-left transition-colors ${
                currentDraft.mode === option.value
                  ? "border-[var(--color-primary)] bg-[var(--color-primary-light)]"
                  : "border-[var(--color-border)] hover:border-[var(--color-primary-border)]"
              }`}
            >
              <p className="text-sm font-medium text-[var(--color-foreground)]">{option.label}</p>
              <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">{option.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(20rem,0.7fr)]">
        <div className="space-y-6">
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
              <Calendar className="h-4 w-4 text-[var(--color-primary)]" />
              Schedule Pattern
            </h2>
            {currentDraft.mode === "scheduled_replay" ? (
              <ScheduledReplayFields draft={currentDraft} setDraft={setDraft} />
            ) : null}
            {currentDraft.mode === "just_in_time" ? (
              <JustInTimeFields draft={currentDraft} setDraft={setDraft} />
            ) : null}
            {currentDraft.mode === "automated_series" ? (
              <AutomatedSeriesFields draft={currentDraft} setDraft={setDraft} />
            ) : null}
            {currentDraft.mode === "on_demand" ? (
              <p className="rounded-xl border border-dashed border-[var(--color-border)] px-4 py-4 text-sm text-[var(--color-muted-foreground)]">
                This webinar is available immediately. No recurring schedule is required.
              </p>
            ) : null}
          </div>

          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
              <TimerReset className="h-4 w-4 text-[var(--color-primary)]" />
              Simulated Attendance
            </h2>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Default count">
                <input
                  type="number"
                  min="0"
                  value={currentDraft.simulatedAttendeeCount}
                  onChange={(event) => setDraft((prev) => ({
                    ...(prev ?? DEFAULT_DRAFT),
                    simulatedAttendeeCount: Number(event.target.value || 0),
                  }))}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
              </Field>
              <Field label="Range min">
                <input
                  type="number"
                  min="0"
                  value={currentDraft.simulatedAttendeeMin}
                  onChange={(event) => setDraft((prev) => ({
                    ...(prev ?? DEFAULT_DRAFT),
                    simulatedAttendeeMin: Number(event.target.value || 0),
                  }))}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
              </Field>
              <Field label="Range max">
                <input
                  type="number"
                  min="0"
                  value={currentDraft.simulatedAttendeeMax}
                  onChange={(event) => setDraft((prev) => ({
                    ...(prev ?? DEFAULT_DRAFT),
                    simulatedAttendeeMax: Number(event.target.value || 0),
                  }))}
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
              </Field>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
              <Clock className="h-4 w-4 text-[var(--color-primary)]" />
              Upcoming Sessions
            </h2>
            <button
              type="button"
              onClick={refetch}
              className="inline-flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {data?.nextStartAt ? (
              <div className="rounded-xl border border-[var(--color-primary)]/30 bg-[var(--color-primary-light)] px-4 py-3">
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                  Next session
                </p>
                <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">
                  {formatSessionTimestamp(data.nextStartAt)}
                </p>
              </div>
            ) : null}

            {upcomingSessions.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[var(--color-border)] px-4 py-4 text-sm text-[var(--color-muted-foreground)]">
                Save a schedule to preview upcoming sessions.
              </p>
            ) : (
              upcomingSessions.map((session) => (
                <div key={session.startsAt} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3">
                  <p className="text-sm font-medium text-[var(--color-foreground)]">{session.label}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {formatSessionTimestamp(session.startsAt)}
                  </p>
                </div>
              ))
            )}
          </div>

          {saveError ? (
            <div className="mt-4 rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
              {saveError}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ScheduledReplayFields({
  draft,
  setDraft,
}: {
  draft: DraftState;
  setDraft: Dispatch<SetStateAction<DraftState | null>>;
}) {
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
          Weekdays
        </p>
        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((label, index) => {
            const isSelected = draft.scheduledReplay.dayOfWeek.includes(index);
            return (
              <button
                key={label}
                type="button"
                onClick={() => setDraft((prev) => {
                  const base = prev ?? DEFAULT_DRAFT;
                  const selected = new Set(base.scheduledReplay.dayOfWeek);
                  if (selected.has(index)) {
                    selected.delete(index);
                  } else {
                    selected.add(index);
                  }
                  return {
                    ...base,
                    scheduledReplay: {
                      ...base.scheduledReplay,
                      dayOfWeek: Array.from(selected).sort((left, right) => left - right),
                    },
                  };
                })}
                className={`rounded-md border px-2 py-2 text-xs font-medium ${
                  isSelected
                    ? "border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-foreground)]"
                    : "border-[var(--color-border)] text-[var(--color-muted-foreground)]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Time of day">
          <input
            type="time"
            value={draft.scheduledReplay.timeOfDay}
            onChange={(event) => setDraft((prev) => ({
              ...(prev ?? DEFAULT_DRAFT),
              scheduledReplay: {
                ...(prev ?? DEFAULT_DRAFT).scheduledReplay,
                timeOfDay: event.target.value,
              },
            }))}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </Field>
        <Field label="Timezone">
          <input
            type="text"
            value={draft.scheduledReplay.timezone}
            onChange={(event) => setDraft((prev) => ({
              ...(prev ?? DEFAULT_DRAFT),
              scheduledReplay: {
                ...(prev ?? DEFAULT_DRAFT).scheduledReplay,
                timezone: event.target.value || "UTC",
              },
            }))}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          />
        </Field>
      </div>
    </div>
  );
}

function JustInTimeFields({
  draft,
  setDraft,
}: {
  draft: DraftState;
  setDraft: Dispatch<SetStateAction<DraftState | null>>;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <Field label="Interval">
        <select
          value={String(draft.justInTime.intervalMinutes)}
          onChange={(event) => setDraft((prev) => ({
            ...(prev ?? DEFAULT_DRAFT),
            justInTime: {
              ...(prev ?? DEFAULT_DRAFT).justInTime,
              intervalMinutes: Number(event.target.value),
            },
          }))}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          {[15, 30, 45, 60].map((value) => (
            <option key={value} value={value}>{value} minutes</option>
          ))}
        </select>
      </Field>
      <Field label="Available from">
        <input
          type="number"
          min="0"
          max="23"
          value={draft.justInTime.availableHoursStart}
          onChange={(event) => setDraft((prev) => ({
            ...(prev ?? DEFAULT_DRAFT),
            justInTime: {
              ...(prev ?? DEFAULT_DRAFT).justInTime,
              availableHoursStart: Number(event.target.value || 0),
            },
          }))}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
      </Field>
      <Field label="Available until">
        <input
          type="number"
          min="1"
          max="24"
          value={draft.justInTime.availableHoursEnd}
          onChange={(event) => setDraft((prev) => ({
            ...(prev ?? DEFAULT_DRAFT),
            justInTime: {
              ...(prev ?? DEFAULT_DRAFT).justInTime,
              availableHoursEnd: Number(event.target.value || 24),
            },
          }))}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
      </Field>
    </div>
  );
}

function AutomatedSeriesFields({
  draft,
  setDraft,
}: {
  draft: DraftState;
  setDraft: Dispatch<SetStateAction<DraftState | null>>;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <Field label="Day offsets">
        <input
          type="text"
          value={draft.automatedSeries.days.join(", ")}
          onChange={(event) => setDraft((prev) => ({
            ...(prev ?? DEFAULT_DRAFT),
            automatedSeries: {
              ...(prev ?? DEFAULT_DRAFT).automatedSeries,
              days: parseDaysInput(event.target.value),
            },
          }))}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          placeholder="1, 3, 7"
        />
      </Field>
      <Field label="Time of day">
        <input
          type="time"
          value={draft.automatedSeries.timeOfDay}
          onChange={(event) => setDraft((prev) => ({
            ...(prev ?? DEFAULT_DRAFT),
            automatedSeries: {
              ...(prev ?? DEFAULT_DRAFT).automatedSeries,
              timeOfDay: event.target.value,
            },
          }))}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
      </Field>
      <Field label="Timezone">
        <input
          type="text"
          value={draft.automatedSeries.timezone}
          onChange={(event) => setDraft((prev) => ({
            ...(prev ?? DEFAULT_DRAFT),
            automatedSeries: {
              ...(prev ?? DEFAULT_DRAFT).automatedSeries,
              timezone: event.target.value || "UTC",
            },
          }))}
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
      </Field>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="font-medium text-[var(--color-foreground)]">{label}</span>
      {children}
    </label>
  );
}

function buildDraftState(data: EvergreenScheduleViewModel): DraftState {
  return {
    mode: data.mode,
    scheduledReplay: {
      dayOfWeek: isScheduledReplayConfig(data.config) ? data.config.dayOfWeek : DEFAULT_DRAFT.scheduledReplay.dayOfWeek,
      timeOfDay: isScheduledReplayConfig(data.config) ? data.config.timeOfDay : DEFAULT_DRAFT.scheduledReplay.timeOfDay,
      timezone: isScheduledReplayConfig(data.config) ? data.config.timezone : DEFAULT_DRAFT.scheduledReplay.timezone,
    },
    justInTime: {
      intervalMinutes: isJustInTimeConfig(data.config) ? data.config.intervalMinutes : DEFAULT_DRAFT.justInTime.intervalMinutes,
      availableHoursStart: isJustInTimeConfig(data.config) ? data.config.availableHoursStart : DEFAULT_DRAFT.justInTime.availableHoursStart,
      availableHoursEnd: isJustInTimeConfig(data.config) ? data.config.availableHoursEnd : DEFAULT_DRAFT.justInTime.availableHoursEnd,
    },
    automatedSeries: {
      days: isAutomatedSeriesConfig(data.config) ? data.config.days : DEFAULT_DRAFT.automatedSeries.days,
      timeOfDay: isAutomatedSeriesConfig(data.config) ? data.config.timeOfDay : DEFAULT_DRAFT.automatedSeries.timeOfDay,
      timezone: isAutomatedSeriesConfig(data.config) ? data.config.timezone : DEFAULT_DRAFT.automatedSeries.timezone,
    },
    simulatedAttendeeCount: data.simulatedAttendeeCount ?? 0,
    simulatedAttendeeMin: data.simulatedAttendeeRange?.min ?? 0,
    simulatedAttendeeMax: data.simulatedAttendeeRange?.max ?? 0,
  };
}

function buildConfigPayload(draft: DraftState) {
  switch (draft.mode) {
    case "scheduled_replay":
      return draft.scheduledReplay;
    case "just_in_time":
      return draft.justInTime;
    case "automated_series":
      return draft.automatedSeries;
    case "on_demand":
    default:
      return {};
  }
}

function parseDaysInput(value: string) {
  const parsed = value
    .split(",")
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isInteger(entry) && entry >= 0 && entry <= 365);

  return parsed.length > 0 ? parsed : DEFAULT_DRAFT.automatedSeries.days;
}

function isScheduledReplayConfig(value: unknown): value is ScheduledReplayConfig {
  return typeof value === "object" && value !== null && Array.isArray((value as ScheduledReplayConfig).dayOfWeek);
}

function isJustInTimeConfig(value: unknown): value is JustInTimeConfig {
  return typeof value === "object" && value !== null && typeof (value as JustInTimeConfig).intervalMinutes === "number";
}

function isAutomatedSeriesConfig(value: unknown): value is AutomatedSeriesConfig {
  return typeof value === "object" && value !== null && Array.isArray((value as AutomatedSeriesConfig).days);
}

function formatSessionTimestamp(value: string) {
  return formatDateTime(value);
}

function EvergreenSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <Skeleton className="h-8 w-56 rounded" />
      {[1, 2, 3].map((item) => (
        <SkeletonCard key={item} className="rounded-lg border-[var(--color-border)] p-4" />
      ))}
    </div>
  );
}
